import RootStore from '@App/stores/root-store'
import { action, makeObservable, observable, runInAction } from 'mobx'
import { WorkModel } from '@Entities/work/work'
import { CLEAR_WORK_DRAFT } from '@Const/work-registration-forms'

export default class ClaimToolStore {
    private rootStore: RootStore

    private _invalidPublishers = false

    constructor(rootStore: RootStore) {
        makeObservable<ClaimToolStore, '_invalidPublishers'>(this, {
            _invalidPublishers: observable,
            setInvalidPublishers: action,
        })
        this.rootStore = rootStore
    }

    get invalidPublishers(): boolean {
        return this._invalidPublishers
    }

    public setInvalidPublishers(isInvalid: boolean) {
        this._invalidPublishers = isInvalid
    }

    public onNextValidatePublishers = () => {
        const hasValidPublishers = this.validateWorkHasEligiblePublisher()
        runInAction(() => (this._invalidPublishers = !hasValidPublishers))
        return hasValidPublishers
    }

    private validateWorkHasEligiblePublisher = () => {
        const workDetails: WorkModel | undefined = this.rootStore.workStore.workDraftLocal
        return workDetails?.originalPublishers
            ? workDetails.originalPublishers
                  .flatMap((op) => op.administratorPublishers)
                  .concat(workDetails.originalPublishers)
                  .map((pub) => pub.amend)
                  .some((amend) => !!amend)
            : false
    }

    private validateClaimedWork = (workToValidate: WorkModel) => {
        const hasComposer = !!workToValidate.writers.filter(
            (writer) => writer.roleCode === 10 || writer.roleCode === 11
        ).length

        const isExistingWork = workToValidate.isComplete
        if (!isExistingWork && !hasComposer) {
            this.rootStore.popupStore.setAlertData(
                'Cannot currently claim shares on this work due to missing Composer or Composer/Author. Please reach out to Support for assistance.',
                true
            )
        } else {
            return true
        }

        return hasComposer
    }

    private clearWorkHistory = () => {
        this.rootStore.workStore.destroyViewWork()
        this.rootStore.workStore.setWorkDraftToLocal(CLEAR_WORK_DRAFT)
        this.rootStore.claimingToolSearchStore.resetClaimSearchCriteriaIfNeeded()
    }

    public resetClaimingToolWizard = () => {
        this.clearWorkHistory()
        this.rootStore.workStore.setWizardStepValidation(false)
        this.rootStore.wizardFlowStore.resetIndex()
    }

    public submitClaim = async () => {
        const workToSubmit = this.rootStore.workStore.workDraftLocal

        if (!workToSubmit) {
            return
        }

        workToSubmit.isClaimingTool = true
        // TODO - Hopefully handle this validation in a more elegant manner with react-hook-forms
        if (!this.validateClaimedWork(workToSubmit)) {
            return
        }

        const submittedClaim = await this.rootStore.workStore.createWork(
            workToSubmit,
            this.rootStore.rightsholderStore.currentRightsholderId
        )
        this.rootStore.popupStore.setAlertData('Claim Submitted Successfully', false)
        return submittedClaim
    }
}
